import React, { useMemo, useState } from 'react';
import { SessionData } from '../hooks/useStatistics';
import { useTheme } from '../hooks/useTheme';

interface TimeOfDayChartProps {
  sessions: SessionData[];
  formatTime: (seconds: number) => string;
}

const TimeOfDayChart: React.FC<TimeOfDayChartProps> = ({ sessions, formatTime }) => {
  const { theme } = useTheme();
  const [rangeDays, setRangeDays] = useState<number>(30); // 7 / 30 / 90 / 365
  const [metric, setMetric] = useState<'minutes' | 'words'>('minutes');

  // Utilities
  const toLocalDateKey = (d: Date): string => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(d);
  };

  const filteredSessions = useMemo(() => {
    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setDate(cutoff.getDate() - rangeDays + 1);
    return sessions.filter((s) => new Date(s.date) >= cutoff);
  }, [sessions, rangeDays]);

  // 7 x 24 matrix of minutes
  type Matrix = number[][]; // [7][24]
  const { matrixMinutes, matrixWords, hourTotalsMinutes, hourTotalsWords, firstStartsPerDay } = useMemo(() => {
    const mMin: Matrix = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    const mWords: Matrix = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
    const hourMin = Array.from({ length: 24 }, () => 0);
    const hourW = Array.from({ length: 24 }, () => 0);
    const firstStarts = new Map<string, number>(); // dayKey -> minutesAfterMidnight

    filteredSessions.forEach((s) => {
      const start = new Date(s.date);
      const durationSec = Math.max(0, Math.round(s.sessionDuration || 0));
      const end = new Date(start.getTime() + durationSec * 1000);
      const words = Math.max(0, s.wordCount || 0);

      // Track earliest start per local day
      const startKey = toLocalDateKey(start);
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      if (!firstStarts.has(startKey) || startMinutes < (firstStarts.get(startKey) as number)) {
        firstStarts.set(startKey, startMinutes);
      }

      if (durationSec <= 0) {
        // No duration, attribute all words to start hour
        if (words > 0) {
          const di = (start.getDay() + 6) % 7;
          const h = start.getHours();
          mWords[di][h] += words;
          hourW[h] += words;
        }
        return;
      }

      const totalMinutes = durationSec / 60;
      let cursor = new Date(start);
      while (cursor < end) {
        const nextHour = new Date(cursor);
        nextHour.setMinutes(60, 0, 0); // move to top of the next hour
        const segmentEnd = nextHour < end ? nextHour : end;
        const minutes = Math.max(0, (segmentEnd.getTime() - cursor.getTime()) / 60000);

        const dayIndex = (cursor.getDay() + 6) % 7; // Mon=0 ... Sun=6
        const hour = cursor.getHours();
        mMin[dayIndex][hour] += minutes;
        hourMin[hour] += minutes;

        const wordsForSegment = totalMinutes > 0 ? (words * (minutes / totalMinutes)) : 0;
        if (wordsForSegment > 0) {
          mWords[dayIndex][hour] += wordsForSegment;
          hourW[hour] += wordsForSegment;
        }

        cursor = segmentEnd;
      }
    });

    return {
      matrixMinutes: mMin,
      matrixWords: mWords,
      hourTotalsMinutes: hourMin,
      hourTotalsWords: hourW,
      firstStartsPerDay: Array.from(firstStarts.values())
    };
  }, [filteredSessions]);

  // Use 95th percentile to avoid one outlier flattening the chart
  const percentile = (arr: number[], p: number) => {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * (sorted.length - 1)));
    return sorted[idx];
  };

  const maxHeatValue = useMemo(() => {
    const values: number[] = [];
    const src = metric === 'minutes' ? matrixMinutes : matrixWords;
    src.forEach((row) => row.forEach((v) => values.push(v)));
    const filtered = values.filter((v) => v > 0);
    const p90 = percentile(filtered, 90);
    const max = Math.max(p90, ...filtered, 0);
    return max === 0 ? 1 : max;
  }, [matrixMinutes, matrixWords, metric]);

  const hasAnyActivity = useMemo(() => {
    const src = metric === 'minutes' ? matrixMinutes : matrixWords;
    return src.some((row) => row.some((v) => v > 0));
  }, [matrixMinutes, matrixWords, metric]);

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Callouts
  const primeTimeWindow = useMemo(() => {
    // rolling 3-hour window across 24h using hourTotals (minutes)
    const totals = metric === 'minutes' ? hourTotalsMinutes : hourTotalsWords;
    if (totals.every((v) => v === 0)) return null;
    let bestStart = 0;
    let bestValue = -1;
    for (let i = 0; i < 24; i++) {
      const v = totals[i] + totals[(i + 1) % 24] + totals[(i + 2) % 24];
      if (v > bestValue) {
        bestValue = v;
        bestStart = i;
      }
    }
    const end = (bestStart + 3) % 24;
    const fmt = (h: number) => {
      const d = new Date();
      d.setHours(h, 0, 0, 0);
      return new Intl.DateTimeFormat('en-US', { hour: 'numeric' }).format(d);
    };
    return `${fmt(bestStart)}–${fmt(end)}`;
  }, [hourTotalsMinutes, hourTotalsWords, metric]);

  const typicalStartTime = useMemo(() => {
    if (firstStartsPerDay.length === 0) return null;
    const sorted = [...firstStartsPerDay].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const minutes = sorted.length % 2 === 1 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
    const d = new Date();
    d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(d);
  }, [firstStartsPerDay]);

  // Heat colors (light/dark/cyberpunk aware)
  const colorScale = (value: number): string => {
    const t = Math.min(1, value / maxHeatValue);
    const level = Math.sqrt(t); // ease a bit
    if (theme === 'light') {
      // from pale green to deep green
      const start = [235, 246, 238]; // #ebf6ee
      const end = [33, 110, 57];     // #216e39
      const rgb = start.map((s, i) => Math.round(s + (end[i] - s) * level));
      return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
    }
    if (theme === 'cyberpunk') {
      const start = [26, 26, 48]; // dark
      const end = [0, 255, 209];
      const rgb = start.map((s, i) => Math.round(s + (end[i] - s) * level));
      return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
    }
    if (theme === 'edge') {
      const start = [21, 26, 43];
      const end = [0, 234, 255];
      const rgb = start.map((s, i) => Math.round(s + (end[i] - s) * level));
      return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
    }
    // default dark
    const start = [33, 38, 45];
    const end = [57, 211, 83];
    const rgb = start.map((s, i) => Math.round(s + (end[i] - s) * level));
    return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  };

  // Histogram scaling: linear relative to the max of the selected metric
  const histogramMax = useMemo(() => {
    const arr = metric === 'minutes' ? hourTotalsMinutes : hourTotalsWords;
    const maxVal = Math.max(...arr, 0);
    return maxVal > 0 ? maxVal : 1;
  }, [hourTotalsMinutes, hourTotalsWords, metric]);

  const scaleHistogramHeight = (value: number): number => {
    if (value <= 0) return 0;
    const ratio = Math.max(0, Math.min(1, value / histogramMax));
    // Leave a touch of headroom at the top so the tallest bar isn't clipped
    return ratio * 96; // percent
  };

  if (!hasAnyActivity) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">⏰ Time of Day</h2>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>No activity yet. Your writing-by-hour chart will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg mb-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-2">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">⏰ Time of Day</h2>
          <div className="hidden md:flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400">
            {primeTimeWindow && (
              <div className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700">Prime time: <span className="font-medium">{primeTimeWindow}</span></div>
            )}
            {typicalStartTime && (
              <div className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700">Typical start: <span className="font-medium">{typicalStartTime}</span></div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className="flex items-center gap-1 mr-2">
            {(['minutes','words'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMetric(m)}
                className={`px-2 py-1 rounded border transition-colors ${metric === m ? 'bg-green-600 text-white border-green-600' : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                title={m === 'minutes' ? 'Heat uses active minutes' : 'Heat uses words'}
              >
                {m === 'minutes' ? 'Minutes' : 'Words'}
              </button>
            ))}
          </div>
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setRangeDays(d)}
              className={`px-2 py-1 rounded border transition-colors ${rangeDays === d ? 'bg-primary text-white border-primary' : 'bg-transparent border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              title={`Last ${d} days`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Callouts for smaller screens */}
      <div className="md:hidden mb-3 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
        {primeTimeWindow && (
          <div className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700">Prime time: <span className="font-medium">{primeTimeWindow}</span></div>
        )}
        {typicalStartTime && (
          <div className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700">Start: <span className="font-medium">{typicalStartTime}</span></div>
        )}
      </div>

      {/* 7x24 Heatmap */}
      <div className="w-full overflow-x-auto">
        <div className="min-w-[860px]">
          <div className="grid grid-cols-[auto_1fr] gap-2">
            {/* Day labels */}
            <div className="flex flex-col gap-[6px] mt-[18px]">
              {dayLabels.map((d) => (
                <div key={d} className="h-6 text-xs text-gray-600 dark:text-gray-400 flex items-center justify-end pr-1 w-10">{d}</div>
              ))}
            </div>
            {/* Heatmap grid */}
            <div className="overflow-hidden">
              <div className="relative">
                <div className="grid" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))', rowGap: '6px', columnGap: '3px' }}>
                  {(metric === 'minutes' ? matrixMinutes : matrixWords).map((row, rowIdx) => (
                    <React.Fragment key={rowIdx}>
                      {row.map((val, colIdx) => (
                        <div
                          key={`${rowIdx}-${colIdx}`}
                          className="h-6 rounded-sm border border-gray-200 dark:border-gray-700"
                          style={{ backgroundColor: val > 0 ? colorScale(val) : (theme === 'light' ? '#f3f4f6' : '#1f2937') }}
                          title={`${dayLabels[rowIdx]} ${String(colIdx).padStart(2, '0')}:00–${String((colIdx + 1) % 24).padStart(2, '0')}:00 • ${Math.round(val)} ${metric === 'minutes' ? 'min' : 'words'}`}
                        />)
                      )}
                    </React.Fragment>
                  ))}
                </div>
                {/* Hour ticks */}
                <div className="mt-2 flex justify-between text-[10px] text-gray-400 dark:text-gray-500">
                  {[0, 3, 6, 9, 12, 15, 18, 21, 23].map((h) => (
                    <span key={h}>{h}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 24-bar histogram */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Hourly breakdown</h3>
        </div>
        <div className="w-full overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="relative h-48 flex items-end gap-1">
              {(metric === 'minutes' ? hourTotalsMinutes : hourTotalsWords).map((value, hour) => {
                const height = scaleHistogramHeight(value);
                return (
                  <div key={hour} className="flex-1 h-full flex flex-col items-center group">
                    <div className="w-full h-full flex items-end">
                      <div
                        className="w-full rounded-sm bg-green-500/90 hover:bg-green-500 transition-all shadow-sm"
                        style={{ height: `${Math.max(0, Math.min(100, height))}%`, minHeight: value > 0 ? 6 : 0 }}
                        title={`${String(hour).padStart(2, '0')}:00–${String((hour + 1) % 24).padStart(2, '0')}:00 • ${Math.round(value)} ${metric === 'minutes' ? 'min' : 'words'}`}
                      />
                    </div>
                    <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">{hour}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeOfDayChart;


